import React from "react";
import styled from "styled-components";

interface HiddenDescriptionProps {
  instructionText?: string;
  "aria-describedby"?: string;
}

const HiddenDescription = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const withHiddenDescription = <
  P extends { "aria-describedby"?: string },
>(
  BaseComponent: React.ComponentType<P>,
): React.FC<P & HiddenDescriptionProps> => {
  const WrappedComponent: React.FC<P & HiddenDescriptionProps> = ({
    instructionText,
    ...props
  }) => {
    const instructionId = React.useId();
    const hasInstructionText = Boolean(
      instructionText && instructionText.trim().length > 0,
    );
    const existingDescriptionId = props["aria-describedby"];
    const describedBy = [
      existingDescriptionId,
      hasInstructionText ? instructionId : undefined,
    ]
      .filter(Boolean)
      .join(" ");

    const baseProps = {
      ...(props as P),
      "aria-describedby": describedBy || undefined,
    };

    return (
      <>
        <BaseComponent {...baseProps} />
        {hasInstructionText ? (
          <HiddenDescription id={instructionId}>
            {instructionText}
          </HiddenDescription>
        ) : null}
      </>
    );
  };

  WrappedComponent.displayName = `withHiddenDescription(${
    BaseComponent.displayName || BaseComponent.name || "Component"
  })`;

  return WrappedComponent;
};
